import React, { useEffect, useRef, useState } from "react";

import "./HorizontalScroller.scss";

interface Props {
  children: React.ReactNode;
  disableWheelScroll?: boolean;
}

const WHEEL_SCROLL_SPEED = 0.5;
const HOVER_SCROLL_SPEED = 8;

function HorizontalScroller(props: Props) {
  const { children, disableWheelScroll } = props;

  const scrollRef = useRef<HTMLDivElement | null>(null);

  const autoScrollRef = useRef<number | null>(null);

  const [isOverflowing, setIsOverflowing] = useState(false);

  const leftFadeRef = useRef<HTMLDivElement | null>(null);
  const rightFadeRef = useRef<HTMLDivElement | null>(null);

  const updateFadeVisibility = () => {
    const element = scrollRef.current;

    const leftFade = leftFadeRef.current;
    const rightFade = rightFadeRef.current;

    if (!element || !leftFade || !rightFade) return;

    const scrollLeft = element.scrollLeft;

    const maxScroll = element.scrollWidth - element.clientWidth;

    const threshold = 12;

    leftFade.classList.toggle(
      "horizontal-scroller__fade--visible",
      scrollLeft > threshold,
    );

    rightFade.classList.toggle(
      "horizontal-scroller__fade--visible",
      scrollLeft < maxScroll - threshold,
    );
  };

  // overflow detection

  useEffect(() => {
    const element = scrollRef.current;

    if (!element) return;

    const checkOverflow = () => {
      const hasOverflow = element.scrollWidth > element.clientWidth;

      setIsOverflowing(hasOverflow);

      updateFadeVisibility();
    };

    checkOverflow();

    // Check again after a delay to catch late-rendered children
    const timeoutId = setTimeout(checkOverflow, 100);

    const resizeObserver = new ResizeObserver(checkOverflow);

    resizeObserver.observe(element);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [children]);

  // scroll listener

  useEffect(() => {
    const element = scrollRef.current;

    if (!element) return;

    updateFadeVisibility();

    element.addEventListener("scroll", updateFadeVisibility);

    return () => {
      element.removeEventListener("scroll", updateFadeVisibility);
    };
  }, []);

  // wheel -> horizontal scroll

  useEffect(() => {
    if (disableWheelScroll) return;

    const element = scrollRef.current;

    if (!element) return;

    const handleWheel = (event: WheelEvent) => {
      if (!isOverflowing) return;

      event.preventDefault();

      element.scrollLeft += event.deltaY * WHEEL_SCROLL_SPEED;
    };

    element.addEventListener("wheel", handleWheel, {
      passive: false,
    });

    return () => {
      element.removeEventListener("wheel", handleWheel);
    };
  }, [isOverflowing]);

  // auto scroll with continuous smooth scrolling

  const stopAutoScroll = () => {
    if (autoScrollRef.current) {
      clearInterval(autoScrollRef.current);

      autoScrollRef.current = null;
    }
  };

  const startAutoScroll = (direction: "left" | "right") => {
    const element = scrollRef.current;

    if (!element) return;

    stopAutoScroll();

    const speed =
      direction === "left" ? -HOVER_SCROLL_SPEED : HOVER_SCROLL_SPEED;

    const scroll = () => {
      const currentScroll = element.scrollLeft;
      const maxScroll = element.scrollWidth - element.clientWidth;

      // Check boundaries
      if (
        (direction === "left" && currentScroll <= 0) ||
        (direction === "right" && currentScroll >= maxScroll)
      ) {
        stopAutoScroll();
        return;
      }

      // Direct manipulation for smooth continuous scroll
      element.scrollLeft += speed;
    };

    // Run at ~60fps for smooth animation
    autoScrollRef.current = setInterval(scroll, 16) as unknown as number;
  };

  // cleanup interval

  useEffect(() => {
    return () => {
      stopAutoScroll();
    };
  }, []);

  return (
    <div className="horizontal-scroller">
      {isOverflowing && (
        <>
          <div
            ref={leftFadeRef}
            className={`
              horizontal-scroller__fade
              horizontal-scroller__fade--left
            `}
            onMouseEnter={() => startAutoScroll("left")}
            onMouseLeave={stopAutoScroll}
          />

          <div
            ref={rightFadeRef}
            className={`
              horizontal-scroller__fade
              horizontal-scroller__fade--right
            `}
            onMouseEnter={() => startAutoScroll("right")}
            onMouseLeave={stopAutoScroll}
          />
        </>
      )}

      {/* tabs */}

      <div className="horizontal-scroller__viewport" ref={scrollRef}>
        <div className="horizontal-scroller__container">{children}</div>
      </div>
    </div>
  );
}

export default HorizontalScroller;
